import {
    Button,
    Card,
    CardContent,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import { If } from "lib/@components/switch"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import Iconify from "minimals-template/components/Iconify"
import { isLegacyScopedModifier } from "../rate-cards/rate-card-modifier-scope"
import { CostFigure } from "./cost-figure"
import { ModifierRows } from "./modifier-rows"

export function CostModifiersSection({ costResult, currencySymbol }) {
    const {
        rateCardModifiers,
        disabledModifierIds,
        onRecalculateModifiers,
        modifiersUpdating,
        hasPendingModifierChanges,
    } = useBoundContext()
    const modifiers = buildModifiers()
    // Scope only earns a column on cards that still hold a legacy trade or facility modifier.
    // Everything created now is whole-regime, so the column would say the same thing every row.
    const showScope = modifiers.some(isLegacyScopedModifier)
    const tradeOptions = (costResult?.tradeBreakdown || []).map(toTradeOption)
    // Must match the engine's base so the running column reconciles with the grand total.
    const baseSubtotal =
        (costResult?.maintenanceSubtotal || 0) +
        sumFixed(costResult?.fixedContracts) +
        (costResult?.ruleFixedAdjustmentsTotal || 0)

    return (
        <If value={modifiers.length > 0}>
            <Card data-cy="modifiers-section">
                <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                            <Typography variant="h6">Modifiers</Typography>
                            <If value={onRecalculateModifiers}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={getRecalculateIcon()}
                                    disabled={!hasPendingModifierChanges || modifiersUpdating}
                                    onClick={onRecalculateModifiers}
                                    data-cy="recalculate-modifiers"
                                >
                                    Recalculate costs
                                </Button>
                            </If>
                        </Stack>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <If value={showScope}>
                                            <TableCell data-cy="modifier-scope-column">Scope (legacy)</TableCell>
                                        </If>
                                        <TableCell>Basis</TableCell>
                                        <TableCell align="center">Enabled</TableCell>
                                        <TableCell align="right">Contribution</TableCell>
                                        <TableCell align="right">Running subtotal</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                Base subtotal
                                            </Typography>
                                        </TableCell>
                                        <If value={showScope}>
                                            <TableCell />
                                        </If>
                                        <TableCell />
                                        <TableCell />
                                        <TableCell />
                                        <TableCell align="right">
                                            <CostFigure value={baseSubtotal} currencySymbol={currencySymbol} />
                                        </TableCell>
                                    </TableRow>
                                    <ModifierRows
                                        modifiers={modifiers}
                                        baseSubtotal={baseSubtotal}
                                        currencySymbol={currencySymbol}
                                        showScope={showScope}
                                        tradeOptions={tradeOptions}
                                    />
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Stack>
                </CardContent>
            </Card>
        </If>
    )

    function buildModifiers() {
        const sourceModifiers = rateCardModifiers?.length
            ? rateCardModifiers
            : costResult?.modifiersEvaluated || costResult?.modifiersApplied || []
        const evaluatedById = new Map()
        const disabledIds = new Set(disabledModifierIds || [])

        for (const evaluated of costResult?.modifiersEvaluated || costResult?.modifiersApplied || []) {
            evaluatedById.set(evaluated.id, evaluated)
        }

        const modifiers = []
        for (const modifier of sourceModifiers) {
            const disabled = disabledIds.has(modifier.id)
            const evaluated = evaluatedById.get(modifier.id)
            const evaluatedDisabled = evaluated?.skipped === true
            modifiers.push({
                ...modifier,
                ...evaluated,
                contribution: evaluated?.contribution || 0,
                disabled,
                pending: disabled !== evaluatedDisabled,
            })
        }
        return modifiers
    }

    function getRecalculateIcon() {
        if (modifiersUpdating) return <CircularProgress size={16} color="inherit" />
        return <Iconify icon="mdi:calculator" />
    }
}

function sumFixed(list) {
    return (list || []).reduce((sum, c) => sum + (c.amount || 0), 0)
}

function toTradeOption(trade) {
    return { id: trade.tradeId, label: trade.tradeName }
}
